import React, { Suspense, useState, useEffect, useMemo } from "react";
import { Await, useLoaderData, useRevalidator, useRouteLoaderData } from "react-router-dom";
import { Button, Col, Container, Form, Row, Collapse, ButtonGroup, Stack, InputGroup, Card, OverlayTrigger, Tooltip } from "react-bootstrap";
import { axiosInstance } from "../../auth/Auth";
import LoadingIndicator from "../../components/LoadingIndicator";
import { PencilFill, TrashFill, XLg, CheckLg, Grid3x3GapFill, Search, List } from "react-bootstrap-icons";
import "./styles.css";
import ConfirmPrompt from "../../components/ConfirmPrompt";
import useSortableData from "../../components/useSortableData";
import { formatDaypartText, formatDemographicText } from "../../components/HelperFunctions";
import ErrorResponse from "../../components/ErrorResponse";
import DemoPicker from "../../components/Pickers/DemoPicker";
import EmailAddressPicker from "../../components/Pickers/EmailAddressPicker";
import StationPicker from "../../components/Pickers/StationPicker";
import MinMaxSpinsPicker from "../../components/Pickers/MinMaxSpinsPicker";
import WeeksPicker from "../../components/Pickers/WeeksPicker";
import FrequencyPicker from "../../components/Pickers/FrequencyPicker";
import PageDesciption from "../../components/PageDesciption";
import MusicTestDaypartPicker from "../../components/Pickers/MusicTestDaypartPicker";
import { useUserFormState } from "../../components/UserSelection";

import { toast } from "react-toastify";

const extraDefaults = { hide_invalid: true, hide_deltas: true };
const MusicTestingReports = () => {
    const moduleName = "musicTestingReports";
    const [errorCode, setErrorCode] = useState(null);
    const { radios, userInfo, musicTestDayparts, demographics, competitorRadios, radiosModuleSettings } = useRouteLoaderData("root");
    const { reports } = useLoaderData();
    const [reportsData, setReportsData] = useState(null);
    const [selectedReport, setSelectedReport] = useState(null);
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [deleteReportId, setDeleteReportId] = useState(null);
    const [searchValue, setSearchValue] = useState("");
    const revalidator = useRevalidator();
    const [formData, setFormData] = useUserFormState(moduleName, extraDefaults);
    console.log(formData);
    const [allRadios, setAllRadios] = useState([]);

    useEffect(() => {
        async function setData() {
            setReportsData((await reports).data);
        }
        setData();
    }, [radios, reports]);

    useEffect(() => {
        async function getEmailAddress() {
            const userInfoData = (await userInfo).data;
            if (userInfoData.email) {
                setFormData(current => ({
                    ...current,
                    email_address: [{ email: userInfoData.email }],
                }));
            }
        }
        getEmailAddress();
    }, [userInfo]);

    useEffect(() => {
        Promise.all([radios, competitorRadios, radiosModuleSettings]).then(([resolvedRadios, resolvedCompetitorRadios]) => {
            const mainRadios = resolvedRadios?.data?.map(r => ({ radio_id: r.radio_id, radio_name: r.radio_name })) ?? [];
            const competitors = resolvedCompetitorRadios?.data?.map(r => ({ radio_id: r.radio_id, radio_name: r.radio_name })) ?? [];

            const mergedRadios = [...mainRadios, ...competitors];
            const uniqueRadiosMap = new Map();
            mergedRadios.forEach(r => {
                if (!uniqueRadiosMap.has(r.radio_id)) {
                    uniqueRadiosMap.set(r.radio_id, r);
                }
            });

            setAllRadios(Array.from(uniqueRadiosMap.values()));
        });
    }, [radios, competitorRadios, radiosModuleSettings]);

    function getMarketDisplayNames(report) {
        const names = allRadios.filter(r => report.radio_ids.includes(r.radio_id)).map(r => r.radio_name);

        return names.length > 0 ? names.join(", ") : "Unknown";
    }

    function getTruncatedRadioNames(names, maxWidth, font = "bold 16px Arial") {
        if (!names || names.length === 0) return "";

        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        context.font = font;

        let text = "";
        for (let i = 0; i < names.length; i++) {
            const newText = text ? text + ", " + names[i] : names[i];
            const width = context.measureText(newText).width;
            if (width > maxWidth - 30) {
                return text + (i < names.length ? "…" : "");
            }
            text = newText;
        }

        return text;
    }

    async function handleFormSubmit(event) {
        event.preventDefault();
        const resolvedUser = (await userInfo).data;
        const input = {
            user_id: resolvedUser.user_id,
            radio_ids: formData.station.map(x => x.radio_id),
            frequency: formData.run_frequency,
            weeks_to_check: formData.number_weeks,
            min_spins: formData.minSpins,
            max_spins: formData.maxSpins,
            listener_group: formData.demo[0].listener_group,
            daypart_id: formData.daypartId ?? 1,
            demo_id: formData.demo[0].demo_id,
            email_addresses: formData.email_address?.map(x => x.email) ?? [],
            hide_deltas: formData.hide_deltas,
            hide_invalid: formData.hide_invalid,
        };

        try {
            const response = await axiosInstance.post("reports/music_testing/", input);
            revalidator.revalidate();
            setFormData({ ...formData, email_address: resolvedUser.email ? [{ email: resolvedUser.email }] : [] });
            setErrorCode(null);
            toast.success(`Music Test Report has been created.`, { autoClose: 5000 });
        } catch (error) {
            setErrorCode(error?.response?.status ?? 500);
            toast.error(`Error trying to create a Music Test Report.`);
        }
    }

    function mapReportToEdit(report) {
        return {
            ...report,
            run_frequency: report.frequency ?? 0,
            number_weeks: report.weeks_to_check ?? 0,
            minSpins: report.min_spins ?? 0,
            maxSpins: report.max_spins ?? 0,
            email_address: (report.email_addresses || []).map(e => (typeof e === "string" ? { email: e } : e)),
        };
    }

    // Next 2 functions handles edit/delete existing reports
    async function handleSaveClick(report) {
        const input = {
            user_id: report.user_id,
            demo_id: report.demo_id,
            daypart_id: report.daypart_id,
            radio_ids: report.radio_ids,
            hide_deltas: report.hide_deltas,
            hide_invalid: report.hide_invalid,
            frequency: report.run_frequency,
            weeks_to_check: report.number_weeks,
            min_spins: report.minSpins,
            max_spins: report.maxSpins,
            email_addresses: report.email_address?.map(x => x.email) ?? [],
        };
        try {
            await axiosInstance.put("/reports/music_testing/" + report.report_id, input);
            setSelectedReport(null);
            revalidator.revalidate();
            toast.success("Report updated successfully!");
        } catch (error) {
            console.error(error);
            toast.error("Error updating report.");
        }
    }

    async function deleteMusicTestReport(id) {
        try {
            const response = await axiosInstance.delete("/reports/music_testing/" + id);
            revalidator.revalidate();
            setDeleteReportId(null);
            setErrorCode(null);
        } catch (error) {
            setErrorCode(error?.response?.status ?? 500);
        }
    }

    const musictTestingReportsMemo = useMemo(() => {
        if (!reportsData) return [];

        return reportsData.filter(x => {
            const emails = Array.isArray(x.email_addresses) ? x.email_addresses.join(",") : x.email_addresses || "";
            const minSpins = x.min_spins?.toString() || "";
            const maxSpins = x.max_spins?.toString() || "";
            const frequency = x.frequency?.toString() || "";
            const weeks = x.weeks_to_check?.toString() || "";
            const search = searchValue.toLowerCase().trim();
            const radioNames = allRadios.filter(r => x.radio_ids?.includes(r.radio_id)).map(r => r.radio_name.toLowerCase());
            const namesJoined = radioNames.join(", ");

            return (
                emails.toLowerCase().includes(search) ||
                minSpins.includes(search) ||
                maxSpins.includes(search) ||
                frequency.includes(search) ||
                weeks.includes(search) ||
                namesJoined.includes(search)
            );
        });
    }, [reportsData, searchValue, allRadios]);

    const { sortedItems, requestSort, sortConfig } = useSortableData(musictTestingReportsMemo);

    return (
        <Container fluid className="p-4 ">
            <Row>
                <Col className="bg-white p-4">
                    <Form className="row" onSubmit={handleFormSubmit}>
                        <Col xs={12} xl className="order-xl-last mb-xl-0 ">
                            <h1 className="text-uppercase">Music Testing Reports</h1>
                            <div className="d-flex flex-column justify-content-between align-items-xl-start">
                                <p className="ps-1">See your reports below, or set up a new one with a click.</p>
                                <Button
                                    type="button"
                                    onClick={() => setAdvancedOpen(!advancedOpen)}
                                    className="btn-ra-yellow text-uppercase px-4 py-xl-1"
                                    aria-controls="music-test-advanced"
                                    aria-expanded={advancedOpen}>
                                    SET UP NEW
                                </Button>
                            </div>
                            <Collapse in={advancedOpen}>
                                <Row className="gy-3">
                                    <Col xs={12} xl={10}>
                                        <Row className="py-2 gy-3" id="music-test-advanced ">
                                            <StationPicker
                                                formData={formData}
                                                setFormData={setFormData}
                                                name={moduleName}
                                                moduleFilterFunction={x => x.module_id === 8}
                                                filterFunction={x => !x.mtt_only}
                                                multiple={true}
                                            />
                                            <Suspense>
                                                <Await resolve={radiosModuleSettings}>
                                                    {resolvedModuleSettings => (
                                                        <DemoPicker
                                                            formData={formData}
                                                            setFormData={setFormData}
                                                            name={moduleName}
                                                            disabled={!formData.station.length}
                                                            filterFunction={x => {
                                                                if (!formData.station.length) return false;
                                                                const station = formData.station[0];
                                                                const hasListenerGroupModule = resolvedModuleSettings.data.find(
                                                                    r => r.radio_id === station.radio_id && r.module_id === 7 && r.enabled
                                                                );

                                                                return (
                                                                    x.music_test &&
                                                                    ((x.data_type === 1 &&
                                                                        station.stream &&
                                                                        (hasListenerGroupModule ? true : x.listener_group === 0)) ||
                                                                        (x.data_type === 2 && station.ppm))
                                                                );
                                                            }}
                                                        />
                                                    )}
                                                </Await>
                                            </Suspense>
                                            <MusicTestDaypartPicker formData={formData} setFormData={setFormData} name={moduleName} />
                                        </Row>
                                        <Row className="gy-3">
                                            <FrequencyPicker formData={formData} setFormData={setFormData} name={moduleName} />
                                            <WeeksPicker formData={formData} setFormData={setFormData} name={moduleName} />
                                            <MinMaxSpinsPicker filterData={formData} setFilterData={setFormData} name={moduleName} />
                                        </Row>
                                        <Row className="mt-3">
                                            <Form.Group as={Col} className="col-12 col-xl-2 ">
                                                <Form.Label className="form-label text-nowrap pe-2">Hide Deltas</Form.Label>
                                                <Form.Switch
                                                    className="form-switch-md  d-inline-block"
                                                    id={`${moduleName}-hide-deltas`}
                                                    size="m"
                                                    checked={formData.hide_deltas}
                                                    onChange={e => setFormData({ ...formData, hide_deltas: e.target.checked })}
                                                />
                                            </Form.Group>
                                            <Form.Group as={Col} className="col-12 col-xl-2  ">
                                                <Form.Label className="form-label text-nowrap pe-2">Hide Invalid</Form.Label>
                                                <Form.Switch
                                                    className="form-switch-md  d-inline-block"
                                                    id={`${moduleName}-hide-invalid`}
                                                    size="m"
                                                    checked={formData.hide_invalid}
                                                    onChange={e => setFormData({ ...formData, hide_invalid: e.target.checked })}
                                                />
                                            </Form.Group>
                                        </Row>
                                        <Row className="mt-2">
                                            <EmailAddressPicker formData={formData} setFormData={setFormData} name={moduleName} />
                                        </Row>
                                    </Col>
                                    <Col xs={12} xl={2} className="mt-3 mt-xl-0 text-center text-xl-end align-self-end ms-auto">
                                        <Button
                                            type="submit"
                                            className="btn-ra-yellow text-uppercase px-5"
                                            disabled={
                                                !formData.station.length ||
                                                !formData.email_address.length ||
                                                !formData.demo.length ||
                                                formData.daypart === "placeholder"
                                            }>
                                            SET UP
                                        </Button>
                                    </Col>
                                </Row>
                            </Collapse>
                        </Col>
                    </Form>
                </Col>
            </Row>
            <hr className="m-2"></hr>
            <Row>
                <Col className="bg-white p-4">
                    {!reportsData?.length > 0 && !errorCode && (
                        <PageDesciption
                            title={"Music Test Reports"}
                            whatList={[
                                "Automate and share your music test lists",
                                "Choose from one week up to one entire year’s worth of data",
                                "Set minimum spins to help filter your lists",
                            ]}
                            knowList={[
                                { text: "Downloads available as Excel, CSV, or HTM" },
                                { text: "Add any number of emails, even if they don’t have a RadioAnalyzer login" },
                                {
                                    text: "Dayparts can be useful for unique perspectives on groups of songs, but for your weekly playlisting be sure to have enough spins on the song to get a good read",
                                },
                            ]}
                            videoSrc="https://www.youtube.com/embed/JEYOVS0Tib4?si=7WlzC9WmWMXY1dtZ"
                        />
                    )}
                    {reportsData && errorCode && <ErrorResponse errorCode={errorCode} />}
                    {!reportsData ? (
                        <span colSpan={12}>
                            <LoadingIndicator />
                        </span>
                    ) : (
                        reportsData.length > 0 && (
                            <>
                                <Stack direction="horizontal" gap={2} className=" flex-wrap">
                                    <p>Here you'll find an overview of your existing music testing repots. Add new music testing reports on the top.</p>
                                </Stack>
                                <Stack direction="horizontal" gap={2} className="mb-3 flex-wrap">
                                    <Col xs={12} xl={8}>
                                        <InputGroup>
                                            <InputGroup.Text className="input-icon">
                                                <Search />
                                            </InputGroup.Text>
                                            <Form.Control
                                                value={searchValue}
                                                onChange={e => {
                                                    setSearchValue(e.target.value);
                                                }}
                                                placeholder="search by radio name, spins or email address"
                                            />
                                        </InputGroup>
                                    </Col>
                                </Stack>
                                <div className="row row-cols-1 row-cols-sm-1 row-cols-md-1 row-cols-lg-2 row-cols-xl-2 row-cols-xxl-3">
                                    {sortedItems.length ? (
                                        sortedItems.map((report, index) => (
                                            <div key={index} className="col mb-4">
                                                <Card className="w-100 ">
                                                    <Card.Header className="bg-ra-dark-blue text-light d-flex justify-content-between align-items-center text-center">
                                                        <OverlayTrigger
                                                            placement="top"
                                                            overlay={<Tooltip id={`tooltip-${report.report_id}`}>{getMarketDisplayNames(report)}</Tooltip>}>
                                                            <div
                                                                ref={el => {
                                                                    if (el) {
                                                                        const radioNames = allRadios
                                                                            .filter(r => report.radio_ids.includes(r.radio_id))
                                                                            .map(r => r.radio_name);
                                                                        const truncated = getTruncatedRadioNames(radioNames, el.offsetWidth, "bold 16px Arial");
                                                                        el.textContent = truncated;
                                                                    }
                                                                }}
                                                                className="fw-bold fs-6 flex-grow-1 text-center"
                                                                style={{
                                                                    overflow: "hidden",
                                                                    whiteSpace: "nowrap",
                                                                }}></div>
                                                        </OverlayTrigger>

                                                        {selectedReport?.report_id === report.report_id ? (
                                                            <ButtonGroup size="sm" className="pe-1">
                                                                <Button
                                                                    variant="success"
                                                                    title="Save"
                                                                    disabled={!selectedReport?.email_address.length}
                                                                    onClick={() => handleSaveClick(selectedReport)}>
                                                                    <CheckLg />
                                                                </Button>
                                                                <Button variant="danger" title="Discard" onClick={() => setSelectedReport(null)}>
                                                                    <XLg />
                                                                </Button>
                                                            </ButtonGroup>
                                                        ) : (
                                                            <ButtonGroup size="sm" className="pe-1">
                                                                <Button
                                                                    variant="primary"
                                                                    title={"Edit"}
                                                                    onClick={() => setSelectedReport(mapReportToEdit(report))}>
                                                                    <PencilFill />
                                                                </Button>
                                                                <Button variant="danger" title={"Delete"} onClick={() => setDeleteReportId(report.report_id)}>
                                                                    <TrashFill />
                                                                </Button>
                                                            </ButtonGroup>
                                                        )}
                                                    </Card.Header>

                                                    <Card.Body>
                                                        <Card.Text>
                                                            {selectedReport?.report_id === report.report_id ? (
                                                                <>
                                                                    <Row className="gy-2">
                                                                        <FrequencyPicker
                                                                            formData={selectedReport}
                                                                            setFormData={setSelectedReport}
                                                                            name={moduleName + "edit"}
                                                                        />
                                                                        <MinMaxSpinsPicker
                                                                            filterData={selectedReport}
                                                                            setFilterData={setSelectedReport}
                                                                            name={moduleName + "edit"}
                                                                        />
                                                                        <WeeksPicker
                                                                            formData={selectedReport}
                                                                            setFormData={setSelectedReport}
                                                                            name={moduleName + "edit"}
                                                                        />
                                                                    </Row>
                                                                </>
                                                            ) : (
                                                                <div className="text-center">
                                                                    <div className="mb-1">
                                                                        <span>
                                                                            send every <strong>{report.frequency} </strong>weeks
                                                                        </span>
                                                                        <div className="vr  d-xl-inline-block mx-2"></div>
                                                                    </div>
                                                                    <div className="mb-1">
                                                                        <span>
                                                                            <strong>
                                                                                {report.min_spins} –{report.max_spins !== 0 ? report.max_spins : " ∞ "}{" "}
                                                                            </strong>
                                                                            spins
                                                                        </span>
                                                                        <div className="vr  d-xl-inline-block mx-2"></div>
                                                                        <span>
                                                                            <strong>{report.weeks_to_check}</strong> weeks
                                                                        </span>
                                                                    </div>
                                                                    <div>
                                                                        <Suspense>
                                                                            <Await resolve={demographics}>
                                                                                {resolvedDemos => {
                                                                                    return (
                                                                                        <div className="mb-1">
                                                                                            <div className="vr d-xl-inline-block mx-2"></div>
                                                                                            <span className="fw-bold">
                                                                                                <div className="vr d-xl-inline-block mx-2"></div>
                                                                                                {formatDemographicText(
                                                                                                    resolvedDemos.data.find(x => x.demo_id === report.demo_id)
                                                                                                )}
                                                                                            </span>
                                                                                        </div>
                                                                                    );
                                                                                }}
                                                                            </Await>
                                                                        </Suspense>
                                                                        <Suspense>
                                                                            <Await resolve={musicTestDayparts}>
                                                                                {resolvedDayparts => (
                                                                                    <span className="fw-bold mb">
                                                                                        {formatDaypartText(
                                                                                            resolvedDayparts.data.find(x => x.daypart_id === report.daypart_id)
                                                                                        )}
                                                                                    </span>
                                                                                )}
                                                                            </Await>
                                                                        </Suspense>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </Card.Text>

                                                        <Card.Text className="music-reports-email-card-edit text-center">
                                                            {selectedReport?.report_id === report.report_id ? (
                                                                <EmailAddressPicker
                                                                    formData={selectedReport}
                                                                    setFormData={setSelectedReport}
                                                                    name={moduleName + "edit"}
                                                                    inputOnly={true}
                                                                />
                                                            ) : (
                                                                <span className="rbt-token p-2">
                                                                    {report.email_addresses.map(e => (typeof e === "string" ? e : e.email)).join(", ")}
                                                                </span>
                                                            )}
                                                        </Card.Text>
                                                    </Card.Body>
                                                </Card>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="row justify-content-center">
                                            <div className="col-12">
                                                <span>No results.</span>
                                            </div>
                                        </div>
                                    )}
                                    <ConfirmPrompt
                                        show={deleteReportId !== null}
                                        handleClose={() => setDeleteReportId(null)}
                                        handleConfirm={async () => {
                                            await deleteMusicTestReport(deleteReportId);
                                        }}
                                    />
                                </div>
                            </>
                        )
                    )}
                </Col>
            </Row>
        </Container>
    );
};

export default MusicTestingReports;
